import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Modal, NotificationToaster, AlertUnsavedData, NotificationInline } from "components/notifications";
import { Button, ButtonSave } from "components/buttons";
import { FormCheckbox, FormDateInput, FormNumericInput, FormSelect } from "components/form-fields";
import { FleetService } from "services";
import { useDirtyData } from 'hooks/useDirtyData';
import { Intent } from "@blueprintjs/core";
import moment from 'moment';

export function EventScheduleModal({
    isOpen,
    mode,
    eventType,
    assetId,
    onClose,
    onSave,
    loading,
    scheduleDefaultDate
}) {
    const activityDateFormat = "YYYY-MM-DDTHH:mm:ss";
    const isSpecialPMI =
        mode === "edit" &&
        eventType &&
        eventType.eventTypeName === "PMI" &&
        eventType.defaultSchedule === "Special Schedule";

    const isSpecialTaxOrMot =
        eventType &&
        eventType.defaultSchedule === "Special Schedule" &&
        (eventType.eventTypeName === "Tax" || eventType.eventTypeName === "MOT");

    const helperText = isSpecialPMI ? (
        <>
            <p>
                Please select the number of events in advance you would like to be added to the Asset page. The dates of these planned events will move in relation to the completion date of the oncoming event.
            </p>
            <p>
                As per government requirements operators must demonstrate 12 months of PMI planning. Depending on the event frequency set above, this events in advance setting will be set at a minimum of 12 month as per government requirements.
            </p>
        </>
    ) : (
        "Please select the number of events in advance you would like to be added to the Asset page. The dates of these planned events will move in relation to the completion date of the oncoming event."
    );

    const defaultCombinedModel = {
        name: "",
        eventInterval: 1,
        eventFrequency: 1,
        eventCount: 1,
        overrideEnabled: false,
        startDate: null,
        disabled: false
    };

    const lastOverrideValues = useRef({
        eventInterval: 1,
        eventFrequency: 1,
        eventCount: 1
    });

    const [originalCombinedData, setOriginalCombinedData] = useState(defaultCombinedModel);
    const [combinedData, setCombinedData] = useState(defaultCombinedModel);
    const isScheduleDirty = useDirtyData(originalCombinedData, combinedData);
    const [blockModalClose, setBlockModalClose] = useState(false);
    const [saving, setSaving] = useState(false);
    const isEnabling = originalCombinedData.disabled === true && combinedData.disabled === false;

    const frequencyOptions = {
        1: "Day(s)",
        2: "Week(s)",
        3: "Month(s)",
        4: "Year(s)"
    };

    const frequencyItems = Object.entries(frequencyOptions).map(([id, name]) => ({
        id,
        name
    }));

    useEffect(() => {
        if (isOpen) {
            let override = false;
            let mapped = {
                name: "",
                eventInterval: 1,
                eventFrequency: 1,
                eventCount: 1,
                startDate: null,
                disabled: false
            };
            const details = eventType.assetEventScheduleDetails || eventType.defaultEventScheduleDetails || {};
            override = eventType.assetSchedule !== null;
            mapped = {
                name: eventType.eventTypeName || "",
                eventInterval: details.repeatFrequency || 1,
                eventFrequency: details.repeatInterval || 1,
                eventCount: details.repeatCount || 1,
                startDate: null,
                disabled: !!eventType.disabled
            };

            lastOverrideValues.current = {
                eventInterval: mapped.eventInterval,
                eventFrequency: mapped.eventFrequency,
                eventCount: mapped.eventCount
            };

            if (mode === "edit" && eventType && eventType.disabled) {
                setOriginalCombinedData({ ...mapped, overrideEnabled: override, disabled: true });
                setCombinedData({ ...mapped, overrideEnabled: override, disabled: false });
            } else {
                setOriginalCombinedData({ ...mapped, overrideEnabled: override });
                setCombinedData({ ...mapped, overrideEnabled: override });
            }
        }
    }, [isOpen, eventType, mode]);

    useEffect(() => {
        if (combinedData.overrideEnabled) {
            lastOverrideValues.current = {
                eventInterval: combinedData.eventInterval,
                eventFrequency: combinedData.eventFrequency,
                eventCount: combinedData.eventCount
            };
        }
    }, [
        combinedData.overrideEnabled,
        combinedData.eventInterval,
        combinedData.eventFrequency,
        combinedData.eventCount
    ]);

    useEffect(() => {
        if (originalCombinedData.name && scheduleDefaultDate) {
            handleFieldChange("startDate", moment(scheduleDefaultDate))
        }
    }, [originalCombinedData?.name, scheduleDefaultDate]);

    function handleFieldChange(field, value) {
        setCombinedData(prev => ({
            ...prev,
            [field]: value
        }));
    }
    function handleOverrideChange(event) {
        const checked = event.target.checked;
        if (!checked && eventType && eventType.defaultEventScheduleDetails) {
            const defaults = eventType.defaultEventScheduleDetails;
            setCombinedData(prev => ({
                ...prev,
                overrideEnabled: false,
                eventInterval: defaults.repeatFrequency || 1,
                eventFrequency: defaults.repeatInterval || 1,
                eventCount: defaults.repeatCount || 1
            }));
        } else if (checked) {
            setCombinedData(prev => ({
                ...prev,
                overrideEnabled: true,
                eventInterval: lastOverrideValues.current.eventInterval,
                eventFrequency: lastOverrideValues.current.eventFrequency,
                eventCount: lastOverrideValues.current.eventCount
            }));
        }
    }

    function handleSave() {
        setSaving(true);
        const repeatFrequency = combinedData.overrideEnabled ? combinedData.eventInterval : null;
        const repeatInterval = combinedData.overrideEnabled ? combinedData.eventFrequency : null;
        const repeatCount = combinedData.overrideEnabled ? combinedData.eventCount : null;

        if (mode === "edit" && eventType && eventType.scheduleId) {
            FleetService.setScheduleOverrides(
                eventType.scheduleId,
                repeatFrequency,
                repeatInterval,
                repeatCount,
                combinedData.disabled,
                isEnabling ? moment(combinedData.startDate).format(activityDateFormat) : undefined
            )
                .then(() => {
                    NotificationToaster.show(Intent.SUCCESS, "Event schedule updated successfully.");
                    setSaving(false);
                    onSave();
                })
                .catch((err) => {
                    let message = "Could not update event schedule. Please try again.";
                    if (err && err.response && err.response.data) {
                        message = typeof err.response.data === "string" ? err.response.data : message;
                    }
                    NotificationToaster.show(Intent.DANGER, message);
                    setSaving(false);
                });
        } else if (mode === "add" && eventType) {
            FleetService.createEventSchedule(
                eventType.eventTypeId,
                Number(assetId),
                moment(combinedData.startDate).format(activityDateFormat),
                repeatFrequency,
                repeatInterval,
                repeatCount
            )
                .then(() => {
                    NotificationToaster.show(Intent.SUCCESS, "Event schedule saved successfully.");
                    setSaving(false);
                    onSave();
                })
                .catch((err) => {
                    let message = "Could not update event schedule. Please try again.";
                    if (err && err.response && err.response.data) {
                        message = typeof err.response.data === "string" ? err.response.data : message;
                    }
                    NotificationToaster.show(Intent.DANGER, message);
                    setSaving(false);
                });
        }
    }

    function handleClose(forceClose = false) {
        if (isScheduleDirty && !forceClose) {
            setBlockModalClose(true);
            return;
        }
        setBlockModalClose(false);
        onClose();
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => handleClose(false)}
            title={"Schedule Details"}
            isCloseButtonShown={true}
            updateStateOnClose={false}
        >
            {(mode === "add" || isEnabling) && (
                <FormDateInput
                    headingText="Start Date"
                    value={combinedData.startDate}
                    onChange={value => handleFieldChange("startDate", value)}
                    minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 1))}
                    maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 2))}
                />
            )}
            {!isSpecialTaxOrMot && (
                <div className="spacer-bottom" >
                    <FormCheckbox
                        label="Override Event Schedule"
                        checked={combinedData.overrideEnabled}
                        onChange={handleOverrideChange}
                        disabled={loading || saving}
                    />
                </div>
            )}
            <p>Events Frequency</p>
            <div style={{ display: "flex", gap: "8px" }}>
                <FormNumericInput
                    id="event-schedule-modal-frequency"
                    selectedValue={combinedData.eventFrequency}
                    onValueChange={value => handleFieldChange("eventFrequency", value)}
                    min={1}
                    max={1000}
                    disabledInput={!combinedData.overrideEnabled || loading || saving}
                    disabled={!combinedData.overrideEnabled || loading || saving}
                />
                <FormSelect
                    id="event-schedule-modal-interval"
                    items={frequencyItems}
                    selectedValue={String(combinedData.eventInterval)}
                    onItemSelect={item => handleFieldChange("eventInterval", item.id)}
                    placeholder="Choose frequency"
                    disabled={!combinedData.overrideEnabled || loading || saving}
                />
            </div>
            <FormNumericInput
                id="event-schedule-modal-event-count"
                headingText="Events in advance (rolling)"
                helperText={!isSpecialTaxOrMot ? helperText : undefined}
                selectedValue={combinedData.eventCount}
                onValueChange={value => handleFieldChange("eventCount", value)}
                min={1}
                max={50}
                disabledInput={!combinedData.overrideEnabled || loading || saving}
                disabled={!combinedData.overrideEnabled || loading || saving}
            />
            <NotificationInline
                id="asset-profile-vehicle-offroad-timer-notification"
                loading={loading}
                show={mode !== "add" && isScheduleDirty}
                text={"The dates of any events already on the system will be updated in relation to these changes."}
                intent="info"
                allowClose={false}
            />
            <div className="bp6-dialog-footer">
                <div className="bp6-dialog-footer-actions">
                    <Button
                        text="Cancel"
                        onClick={() => handleClose(false)}
                        disabled={loading || saving}
                    />
                    <ButtonSave
                        text="Save"
                        onClick={handleSave}
                        disabled={loading || saving}
                        simpleDisabled={
                            !isScheduleDirty ||
                            ((mode === "add" || isEnabling) && !combinedData.startDate)
                        }
                    />
                </div>
            </div>

            <AlertUnsavedData
                isDirty={isScheduleDirty}
                isDirtySamePage={blockModalClose}
                onCancelLeave={() => setBlockModalClose(false)}
                onConfirmLeave={() => handleClose(true)}
            />
        </Modal>
    );
}

EventScheduleModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(["add", "edit"]).isRequired,
    eventType: PropTypes.object,
    assetId: PropTypes.number.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    scheduleDefaultDate: PropTypes.string
};